import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { practiceAPI } from '../../api';
import { Sidebar } from './dashboard_component';

const AiGenerator = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('text');
  const [title, setTitle] = useState('');
  const [text, setText] = useState('');
  const [file, setFile] = useState(null);
  const [numQuestions, setNumQuestions] = useState(20);
  const [difficulty, setDifficulty] = useState('medium');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!title.trim()) return toast.error('Please enter a title');
    if (activeTab === 'text' && !text.trim()) return toast.error('Please paste some text');
    if (activeTab === 'file' && !file) return toast.error('Please select a file');

    setLoading(true);
    try {
      let response;
      if (activeTab === 'text') {
        response = await practiceAPI.generateFromText(title, text, numQuestions, difficulty);
      } else {
        response = await practiceAPI.generateFromFile(title, file, numQuestions, difficulty);
      }
      toast.success(`✅ Generated ${response.data.total_questions} questions!`);
      navigate('/participate/preparena');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'AI generation failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen bg-gradient-to-br from-gray-50 to-blue-50/30">
      <Sidebar isCollapsed={false} onToggle={() => {}} />
      <div className="flex-1 transition-all duration-300 pt-16 lg:pt-8 lg:ml-64">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          {/* Header */}
          <div className="bg-gradient-to-r from-purple-600 to-pink-600 rounded-2xl shadow-lg overflow-hidden mb-8">
            <div className="px-6 py-8">
              <div className="flex items-center gap-3">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                </svg>
                <h1 className="text-2xl font-bold text-white">AI Question Generator</h1>
              </div>
              <p className="text-purple-100 mt-2">Paste text or upload a document – let AI create a custom practice set for you.</p>
            </div>
          </div>

          {/* Form */}
          <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8">
            <form onSubmit={handleSubmit}>
              {/* Title */}
              <div className="mb-6">
                <label className="block text-sm font-semibold text-gray-700 mb-2">Set Title</label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  placeholder="e.g., AI-generated Physics Quiz"
                  required
                />
              </div>

              {/* Tabs */}
              <div className="border-b border-gray-200 mb-6">
                <nav className="flex space-x-4">
                  <button
                    type="button"
                    onClick={() => setActiveTab('text')}
                    className={`py-2 px-4 font-medium transition-all ${
                      activeTab === 'text'
                        ? 'text-purple-600 border-b-2 border-purple-600'
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    Paste Text
                  </button>
                  <button
                    type="button"
                    onClick={() => setActiveTab('file')}
                    className={`py-2 px-4 font-medium transition-all ${
                      activeTab === 'file'
                        ? 'text-purple-600 border-b-2 border-purple-600'
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    Upload Document
                  </button>
                </nav>
              </div>

              {/* Tab Content */}
              {activeTab === 'text' && (
                <div className="mb-6">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Source Text</label>
                  <textarea
                    rows={8}
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                    placeholder="Paste your content here (at least 50 characters)..."
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">Minimum 50 characters. Longer texts will be truncated to 8000 characters.</p>
                </div>
              )}

              {activeTab === 'file' && (
                <div className="mb-6">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Upload File</label>
                  <div className="w-full p-6 border-2 border-dashed border-gray-300 rounded-xl text-center hover:border-purple-500 hover:bg-purple-50 transition-all">
                    <input
                      type="file"
                      accept=".pdf,.docx,.txt"
                      onChange={(e) => setFile(e.target.files[0])}
                      className="hidden"
                      id="fileInput"
                    />
                    <label htmlFor="fileInput" className="cursor-pointer flex flex-col items-center gap-2">
                      <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                      </svg>
                      <span className="text-sm text-gray-600">{file ? file.name : 'Click to select PDF, DOCX, or TXT'}</span>
                    </label>
                  </div>
                </div>
              )}

              {/* Options */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Number of Questions</label>
                  <input
                    type="number"
                    min={1}
                    max={20}
                    value={numQuestions}
                    onChange={(e) => setNumQuestions(parseInt(e.target.value) || 20)}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Difficulty</label>
                  <select
                    value={difficulty}
                    onChange={(e) => setDifficulty(e.target.value)}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500"
                  >
                    <option value="easy">Easy</option>
                    <option value="medium">Medium</option>
                    <option value="hard">Hard</option>
                  </select>
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white font-bold rounded-xl hover:from-purple-700 hover:to-pink-700 transition transform hover:-translate-y-0.5 disabled:opacity-50 disabled:transform-none"
              >
                {loading ? (
                  <div className="flex items-center justify-center gap-2">
                    <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Generating...
                  </div>
                ) : (
                  'Generate Questions'
                )}
              </button>
            </form>
          </div>

          {/* Info */}
          <div className="mt-6 text-sm text-gray-500 text-center">
            <p>💡 Generated questions will appear in your <a href="/participate/preparena" className="text-purple-600 hover:underline">PrepArena</a>.</p>
            <p>📄 Uploaded documents are stored for future model training.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AiGenerator;